// Attribution helpers shared by the marketing dashboard and (Phase 2)
// the Attribution & Funnel screen. All of this derives from prospects in
// PipelineContext — the same rows the server's /marketing/summary reads —
// so counts here must reconcile with wonBySource/wonCount from that API.

import { calculateAnnualizedValue } from '../../utils/pipelineCalculations';
import { parseLocalDate } from './dates';

// Sources we treat as marketing-generated demand for the "marketing
// prospects" KPI. Relationship channels (referral, cross-sell, solicitor,
// coi) are tracked in attribution but are not marketing-machine output.
export const MARKETING_SOURCES = new Set(['website', 'podcast', 'youtube', 'social', 'event']);

// Human labels — mirrors LEAD_SOURCES in pipelineCalculations.js plus the
// synthetic 'unattributed' bucket the server emits for NULL sources.
export const SOURCE_LABELS = {
  'cross-sell': 'Cross-Sell',
  referral: 'Referral',
  solicitor: 'Solicitor (Zoe)',
  coi: 'COI / Partner',
  event: 'Event',
  website: 'Website',
  podcast: 'Podcast',
  youtube: 'YouTube',
  social: 'Social Media',
  other: 'Other',
  unattributed: 'Unattributed',
};

const TERMINAL_STAGES = new Set(['closed-won', 'closed-lost', 'unqualified']);
const BWM_SERVICE_TIERS = new Set(['guided-wealth', 'total-wealth', 'tax-plan', 'investment-only']);

// Mirror of the server's BWM won filter in handleMarketing():
//   division = 'bwm' OR (division IS NULL AND service_tier IN (BWM tiers))
// Keep these in lockstep — the dashboard's deal count must equal the
// API's wonCount or CAC and sourced-won stop reconciling.
export function isBwmProspect(p) {
  if (p.division === 'bwm') return true;
  return !p.division && BWM_SERVICE_TIERS.has(p.serviceTier);
}

export function isWonInYear(p, year) {
  if (p.outcome !== 'won' && p.stage !== 'closed-won') return false;
  if (!p.closedDate) return false;
  return parseLocalDate(p.closedDate).getFullYear() === year;
}

// Closed-won BWM deals for the year, with annualized revenue by source.
// Returns { deals, totalValue, bySource: [{source, count, value, pct}] }.
export function computeWonAttribution(prospects, year) {
  const won = (prospects || []).filter(p => isBwmProspect(p) && isWonInYear(p, year));
  const bySourceMap = {};
  let totalValue = 0;
  for (const p of won) {
    const src = p.source || 'unattributed';
    const value = calculateAnnualizedValue(p.estimatedValue) || 0;
    if (!bySourceMap[src]) bySourceMap[src] = { count: 0, value: 0 };
    bySourceMap[src].count += 1;
    bySourceMap[src].value += value;
    totalValue += value;
  }
  const bySource = Object.entries(bySourceMap)
    .map(([source, v]) => ({
      source,
      count: v.count,
      value: Math.round(v.value),
      pct: won.length > 0 ? v.count / won.length : 0,
    }))
    .sort((a, b) => b.count - a.count || b.value - a.value);
  return { deals: won.length, totalValue: Math.round(totalValue), bySource };
}

// Per-source CRM table for the Attribution screen: every prospect ever,
// with observed win rates (won / decided outcomes) and annualized won
// revenue. All divisions, all time — the dashboard's sourced-won tile
// stays the BWM-year number that reconciles with /marketing/summary.
export function computeSourceTable(prospects) {
  const bySource = {};
  for (const p of prospects || []) {
    const src = p.source || 'unattributed';
    if (!bySource[src]) bySource[src] = { source: src, prospects: 0, won: 0, outcomes: 0, revenue: 0 };
    const row = bySource[src];
    row.prospects += 1;
    if (p.stage === 'closed-won') {
      row.won += 1;
      row.outcomes += 1;
      row.revenue += calculateAnnualizedValue(p.estimatedValue) || 0;
    } else if (p.stage === 'closed-lost') {
      row.outcomes += 1;
    }
  }
  return Object.values(bySource)
    .map(r => ({
      ...r,
      revenue: Math.round(r.revenue),
      winRate: r.outcomes > 0 ? r.won / r.outcomes : null,
    }))
    .sort((a, b) => b.won - a.won || b.revenue - a.revenue || b.prospects - a.prospects);
}

// Pipeline bottom of the visit-to-won funnel, scoped to the same date
// window as the GA4 top (rangeWindow in dates.js).
export function computeFunnelBottom(prospects, { start, end }) {
  const inWindow = (d) => {
    const dt = parseLocalDate(d);
    return dt && dt >= start && dt <= end;
  };
  const newProspects = (prospects || []).filter(p => p.createdDate && inWindow(p.createdDate)).length;
  const won = (prospects || []).filter(p =>
    isBwmProspect(p)
    && (p.outcome === 'won' || p.stage === 'closed-won')
    && p.closedDate && inWindow(p.closedDate)
  );
  const wonValue = won.reduce((s, p) => s + (calculateAnnualizedValue(p.estimatedValue) || 0), 0);
  return { newProspects, wonCount: won.length, wonValue: Math.round(wonValue) };
}

// Open pipeline prospects from marketing sources + how many arrived this month.
export function computeMarketingProspects(prospects, now = new Date()) {
  const open = (prospects || []).filter(p =>
    MARKETING_SOURCES.has(p.source) && !TERMINAL_STAGES.has(p.stage)
  );
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const newThisMonth = open.filter(p =>
    p.createdDate && parseLocalDate(p.createdDate) >= monthStart
  ).length;
  return { open: open.length, newThisMonth };
}
